import * as React from "react";
import { css, keyframes } from "@emotion/react";
import {
  FiAlertTriangle,
  FiCheck,
  FiInfo,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import type { QueuedToast, ToastType } from ".";
import { useDismissTimer } from "./use-dismiss-timer";

type ToastCardProps = {
  toast: QueuedToast;
  onDismiss: (id: number) => void;
};

const toastDurationMs = 5000;
const exitDurationMs = 180;

const icons: Record<ToastType, React.ReactNode> = {
  success: <FiCheck />,
  info: <FiInfo />,
  warning: <FiAlertTriangle />,
  error: <FiXCircle />,
};

export const ToastCard = ({ toast, onDismiss }: ToastCardProps) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [hasFocus, setHasFocus] = React.useState(false);
  const [isExiting, setIsExiting] = React.useState(false);

  const requestDismiss = React.useCallback((): void => {
    setIsExiting(true);
  }, []);

  useDismissTimer({
    durationMs: toastDurationMs,
    isPaused: isHovered || hasFocus,
    isDisabled: isExiting,
    onExpire: requestDismiss,
  });

  React.useEffect(() => {
    if (!isExiting) return;
    const timeoutId = window.setTimeout(
      () => onDismiss(toast.id),
      exitDurationMs,
    );
    return () => window.clearTimeout(timeoutId);
  }, [isExiting, onDismiss, toast.id]);

  const handleBlur = (event: React.FocusEvent<HTMLElement>): void => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setHasFocus(false);
    }
  };

  return (
    <section
      role={toast.type === "error" ? "alert" : "status"}
      css={[
        styles.card,
        styles[toast.type],
        isExiting ? styles.exiting : styles.entering,
      ]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setHasFocus(true)}
      onBlur={handleBlur}
    >
      <span
        css={[styles.icon, toast.type !== "success" && styles.offsetIcon]}
        aria-hidden="true"
      >
        {icons[toast.type]}
      </span>
      <div css={styles.content}>
        <strong css={styles.title}>{toast.title}</strong>
        <p css={styles.detail}>{toast.detail}</p>
      </div>
      <button
        css={styles.close}
        type="button"
        onClick={requestDismiss}
        aria-label={`${toast.title}を閉じる`}
      >
        <FiX aria-hidden="true" />
      </button>
    </section>
  );
};

const enter = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const exit = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(12px); }
`;

const styles = {
  card: css`
    --toast-accent: #1d9b5f;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    width: 100%;
    padding: 16px;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-white);
    color: var(--color-font);
    box-shadow: 0 6px 20px rgb(0 0 0 / 10%);
  `,
  entering: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${enter} 180ms ease-out both;
    }
  `,
  exiting: css`
    pointer-events: none;

    @media (prefers-reduced-motion: no-preference) {
      animation: ${exit} ${exitDurationMs}ms ease-in both;
    }
  `,
  success: css`
    --toast-accent: #1d9b5f;
  `,
  info: css`
    --toast-accent: #2563eb;
  `,
  warning: css`
    --toast-accent: #d97706;
  `,
  error: css`
    --toast-accent: var(--color-danger);
  `,
  icon: css`
    display: grid;
    place-items: center;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--toast-accent);
    color: var(--color-white);
    font-size: 17px;
  `,
  offsetIcon: css`
    & > svg {
      transform: translateY(-1px);
    }
  `,
  content: css`
    min-width: 0;
    flex: 1;
  `,
  title: css`
    display: block;
    line-height: 1.5;
  `,
  detail: css`
    margin: 4px 0 0;
    color: var(--color-font-weak);
    font-size: 14px;
    line-height: 1.5;
    overflow-wrap: anywhere;
  `,
  close: css`
    display: grid;
    place-items: center;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--color-font-weak);
    cursor: pointer;
    font-size: 18px;

    &:hover,
    &:focus-visible {
      background: var(--color-border);
      color: var(--color-font);
      outline: none;
    }
  `,
};
